import React, { memo, useCallback, useEffect } from 'react';
import cn from 'classnames';
import { ValidationError } from 'yup';

import { Button, NumberInput, RadioButton, Select } from '@components/common';
import { ETabsIds } from '@components/routes/AdminPanel/KpiAlertsManagement/AddNewKpiAlertRule/tabsIds';

import { TValueOfStringRecord } from '@interfaces/common/genericWithModifying';
import { TSelectOption } from '@interfaces/components/componentTypes';
import { IAddKpiNotificationScheduleProps, IKpiAlertSchema } from '../interfaces';

import { EScheduleFields } from './enums';

import { hoursOptions, minutesOptions } from '@constants/timeOptions';

import { filterValuesByKeys } from '../utils';

import { MAX_NUMBER_OF_REPETITIONS, MIN_NUMBER_OF_REPETITIONS, scheduleSchema } from './schema';

export const ENotify = {
	immediately: 'ONE_VALUE_OUTSIDE',
	all: 'ALL_VALUES_OUTSIDE',
	customValue: 'CUSTOM',
} as const;

export const EAllowToRenotify = {
	dont: 'DO_NOT_RENOTIFY',
	whenOccurAgain: 'WHEN_OCCURS_AGAIN',
	customValue: 'AFTER_SINCE_PREVIOUS',
} as const;

export type TNotifyEnum = TValueOfStringRecord<typeof ENotify>
export type TAllowToRenotify = TValueOfStringRecord<typeof EAllowToRenotify>

function AddKpiAlertSchedule({
	setActiveTab,
	formMethods,
	formState,
	values,
	negativeHandler,
	isEdit,
	positiveBtnText,
	negativeBtnText,
	positiveHandler,
}: IAddKpiNotificationScheduleProps) {
	const {
		setValue,
		trigger,
		clearErrors,
		register,
		setError,
		control,
		handleSubmit,
	} = formMethods;

	const { errors, dirtyFields } = formState;

	const {
		numberOfRepetitions,
		notify,
		allowToRenotify,
		allowToRenotifyAfterCustomValueHours,
		allowToRenotifyAfterCustomValueMinutes,
	} = values;

	useEffect(() => {
		async function validateFields() {
			try {
				await scheduleSchema.validateAt(EScheduleFields.allowToRenotifyAfterCustomValueHours, values);
				clearErrors(EScheduleFields.allowToRenotifyAfterCustomValueHours);

				await scheduleSchema.validateAt(EScheduleFields.allowToRenotifyAfterCustomValueMinutes, values);
				clearErrors(EScheduleFields.allowToRenotifyAfterCustomValueMinutes);
			} catch (e) {
				return;
			}
		}

		void validateFields();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [values.allowToRenotifyAfterCustomValueHours?.value, values.allowToRenotifyAfterCustomValueMinutes?.value]);

	const scheduleValues = filterValuesByKeys(values, Object.keys(EScheduleFields));

	const onHandleNext = useCallback(async () => {
		if (isEdit) {
			return handleSubmit(positiveHandler)();
		}

		try {
			await scheduleSchema.validate(scheduleValues, { abortEarly: false });

			setActiveTab(ETabsIds.recipient);
		} catch (e) {
			const { message, path, inner } = e as ValidationError;

			if (path) {
				setError(path, { message: message as string });
			} else {
				inner.forEach((item: any) => {
					setError(item.path, { message: item.message });
				});
			}
		}
	}, [
		positiveHandler,
		handleSubmit,
		isEdit,
		scheduleValues,
		setActiveTab,
		setError,
	]);

	useEffect(() => {
		if (dirtyFields.notify && !!dirtyFields.numberOfRepetitions) {
			void trigger(EScheduleFields.numberOfRepetitions);
		}
	}, [dirtyFields.notify, dirtyFields.numberOfRepetitions, notify, trigger]);

	useEffect(() => {
		if (notify !== ENotify.customValue) {
			clearErrors(EScheduleFields.numberOfRepetitions);
		}
	}, [clearErrors, notify, numberOfRepetitions]);

	useEffect(() => {
		if (allowToRenotify !== EAllowToRenotify.customValue) {
			clearErrors(EScheduleFields.allowToRenotifyAfterCustomValueHours);
			clearErrors(EScheduleFields.allowToRenotifyAfterCustomValueMinutes);
		}
	}, [clearErrors, allowToRenotify, numberOfRepetitions]);

	const onChangeNumberOfRepetitions = useCallback((value: string) => {
		setValue(EScheduleFields.numberOfRepetitions, +value, { shouldValidate: true });
	}, [setValue]);

	const onBlurNumberOfRepetitions = () => {
		trigger(EScheduleFields.numberOfRepetitions);
	};

	const onBlurAllowToRenotifyAfterCustomValueHours = () => {
		trigger(EScheduleFields.allowToRenotifyAfterCustomValueHours);
	};

	const onBlurAllowToRenotifyAfterCustomValueMinutes = () => {
		trigger(EScheduleFields.allowToRenotifyAfterCustomValueMinutes);
	};

	const onBackHandler = useCallback(() => {
		negativeHandler(ETabsIds.condition);
	}, [negativeHandler]);

	const setValues = (name: string, value: string): void => {
		setValue(name, value, { shouldValidate: true, shouldDirty: true });
	};

	const onSelectChange = (name: string) => (value: TSelectOption) => {
		setValue(name, value, { shouldValidate: true, shouldDirty: true });
	};

	const isSubmitBtnDisabled = Object.keys(scheduleValues)
		.some((key: string) => !!errors[key as keyof IKpiAlertSchema]);

	return (
		<div className='scheduleContainer display-flex flex-column'>
			<div className='fieldLabel'>
				<span>Notify</span>
				<span className='asterisk'>*</span>
			</div>
			<div className='fieldsContainer display-flex flex-column'>
				<RadioButton
					checked={notify === ENotify.immediately}
					disabled={isEdit}
					id={ENotify.immediately}
					isError={!!errors.notify?.message}
					label='Immediately, as soon as one metric value is outside the threshold'
					name={EScheduleFields.notify}
					register={register}
					setValue={setValues}
					value={ENotify.immediately}
				/>
				<RadioButton
					checked={notify === ENotify.all}
					disabled={isEdit}
					id={ENotify.all}
					isError={!!errors.notify?.message}
					label='If all values are outside the threshold'
					name={EScheduleFields.notify}
					register={register}
					setValue={setValues}
					value={ENotify.all}
				/>
				<div className='display-flex flex-row align-center'>
					<RadioButton
						checked={notify === ENotify.customValue}
						disabled={isEdit}
						id={ENotify.customValue}
						isError={!!errors.notify?.message}
						label='When'
						name={EScheduleFields.notify}
						register={register}
						setValue={setValues}
						value={ENotify.customValue}
					/>
					<div className='customFieldContainer display-flex flex-row ml-5'>
						<NumberInput
							disabled={notify !== ENotify.customValue || isEdit}
							isError={!!errors.numberOfRepetitions?.message}
							maxValue={MAX_NUMBER_OF_REPETITIONS}
							minValue={MIN_NUMBER_OF_REPETITIONS}
							name={EScheduleFields.numberOfRepetitions}
							onBlur={onBlurNumberOfRepetitions}
							onChange={onChangeNumberOfRepetitions}
							placeholder='number'
							step={1}
							value={numberOfRepetitions?.toString()}
						/>
						<span className='fs-regular_14 ml-5 pt-10 no-wrap'>values are outside the threshold </span>
					</div>
				</div>
				<div className='fieldErrorMessage'>
					{
						errors?.numberOfRepetitions?.message as string
					}
				</div>
			</div>
			<div className='fieldLabel'>
				<span>Allow to renotify</span>
				<span className='asterisk'>*</span>
			</div>
			<div className='fieldsContainer display-flex flex-column'>
				<RadioButton
					checked={allowToRenotify === EAllowToRenotify.dont}
					disabled={isEdit}
					id={EAllowToRenotify.dont}
					label='Do not renotify'
					name={EScheduleFields.allowToRenotify}
					register={register}
					setValue={setValues}
					value={EAllowToRenotify.dont}
				/>
				<RadioButton
					checked={allowToRenotify === EAllowToRenotify.whenOccurAgain}
					disabled={isEdit}
					id={EAllowToRenotify.whenOccurAgain}
					label='When occurs again'
					name={EScheduleFields.allowToRenotify}
					register={register}
					setValue={setValues}
					value={EAllowToRenotify.whenOccurAgain}
				/>
				<div className='display-flex flex-row align-center'>
					<RadioButton
						checked={allowToRenotify === EAllowToRenotify.customValue}
						disabled={isEdit}
						id={EAllowToRenotify.customValue}
						label='After'
						name={EScheduleFields.allowToRenotify}
						register={register}
						setValue={setValues}
						value={EAllowToRenotify.customValue}
					/>
					<div className={cn('display-flex align-start flex-gap_10 ml-5', {
						'fieldsContainerError': !!errors?.allowToRenotifyAfterCustomValueHours?.message
							|| !!errors?.allowToRenotifyAfterCustomValueMinutes?.message,
					})}>
						<Select
							control={control}
							disabled={allowToRenotify !== EAllowToRenotify.customValue || isEdit}
							handleChange={onSelectChange(EScheduleFields.allowToRenotifyAfterCustomValueHours)}
							isError={!!errors?.allowToRenotifyAfterCustomValueHours?.message}
							maxMenuHeight={250}
							menuPlacement='top'
							name={EScheduleFields.allowToRenotifyAfterCustomValueHours}
							onBlur={onBlurAllowToRenotifyAfterCustomValueHours}
							options={hoursOptions}
							placeholder='h'
							value={allowToRenotifyAfterCustomValueHours}
						/>
						<div className='pt-10 mr-5'>
							<span className='fieldLabel'>h</span>
						</div>
						<Select
							control={control}
							disabled={allowToRenotify !== EAllowToRenotify.customValue || isEdit}
							handleChange={onSelectChange(EScheduleFields.allowToRenotifyAfterCustomValueMinutes)}
							isError={!!errors?.allowToRenotifyAfterCustomValueMinutes?.message}
							maxMenuHeight={250}
							menuPlacement='top'
							name={EScheduleFields.allowToRenotifyAfterCustomValueMinutes}
							onBlur={onBlurAllowToRenotifyAfterCustomValueMinutes}
							options={minutesOptions}
							placeholder='min'
							value={allowToRenotifyAfterCustomValueMinutes}
						/>
						<div className='pt-10 mr-5'>
							<span className='fieldLabel'>min since previous alert was sent</span>
						</div>
					</div>
				</div>
				<div className='fieldErrorMessage'>
					{
						errors?.allowToRenotifyAfterCustomValueHours?.message as string
						|| errors?.allowToRenotifyAfterCustomValueMinutes?.message as string
					}
				</div>
			</div>
			<div className='buttonsContainer'>
				<Button
					onClick={onBackHandler}
					text={negativeBtnText}
					withoutBackground
				/>
				<Button
					disabled={isSubmitBtnDisabled}
					onClick={onHandleNext}
					text={positiveBtnText}
				/>
			</div>
		</div>
	);
}

export default memo(AddKpiAlertSchedule);
